// API client for Lead Bot Admin

const API_BASE = import.meta.env.VITE_API_BASE || 'https://lead-bot.urowujqjhhsss.workers.dev';

let authToken = localStorage.getItem('lead_bot_token') || '';

export function setToken(token) {
  authToken = token;
  localStorage.setItem('lead_bot_token', token);
}

export function getToken() {
  return authToken;
}

export function clearToken() {
  authToken = '';
  localStorage.removeItem('lead_bot_token');
}

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;

  const headers = {
    'Content-Type': 'application/json',
    ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
    ...options.headers,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearToken();
    throw new Error('Unauthorized');
  }

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

// Auth
export async function verifyToken(token) {
  const saved = authToken;
  setToken(token);

  try {
    await request('/admin/stats');
    return true;
  } catch (e) {
    setToken(saved);
    return false;
  }
}

// Get available leadbot aliases
export async function getLeadbotBots() {
  return request('/admin/leadbot-bots');
}

// Dashboard
export async function getStats(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/stats${query ? `?${query}` : ''}`);
}

// Users
export async function getUsers(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/users${query ? `?${query}` : ''}`);
}

export async function getUser(telegramId, alias) {
  const query = alias ? `?alias=${encodeURIComponent(alias)}` : '';
  return request(`/admin/users/${telegramId}${query}`);
}

export async function deleteUser(telegramId, alias) {
  const query = alias ? `?alias=${encodeURIComponent(alias)}` : '';
  return request(`/admin/users/${telegramId}${query}`, { method: 'DELETE' });
}

// CRM
export async function getCrmStats(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/crm/stats${query ? `?${query}` : ''}`);
}

export async function retryCrmExports() {
  return request('/admin/crm/retry', { method: 'POST' });
}

// Reminders
export async function getReminderStats(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/reminders/stats${query ? `?${query}` : ''}`);
}

// Messages
export async function getMessages(telegramId, alias, params = {}) {
  const queryParams = new URLSearchParams(params);
  if (alias) queryParams.set('alias', alias);
  const query = queryParams.toString();
  return request(`/admin/messages/${telegramId}${query ? `?${query}` : ''}`);
}

export async function sendMessage(telegramId, message) {
  return request('/admin/messages/send', {
    method: 'POST',
    body: JSON.stringify({ telegramId, message }),
  });
}

// Webhook
export async function getWebhookInfo(alias) {
  const query = alias ? `?alias=${encodeURIComponent(alias)}` : '';
  return request(`/admin/webhook/info${query}`);
}

// Bot Management (CRUD)
export async function getBot(alias) {
  return request(`/admin/bots/${encodeURIComponent(alias)}`);
}

export async function saveBot(alias, token, displayName, { force } = {}) {
  return request(`/admin/bots/${encodeURIComponent(alias)}`, {
    method: 'POST',
    body: JSON.stringify({ token, displayName, force }),
  });
}

export async function setupBotWebhook(alias) {
  return request(`/admin/bots/${encodeURIComponent(alias)}/webhook`, {
    method: 'POST',
  });
}

export async function deleteBot(alias) {
  return request(`/admin/bots/${encodeURIComponent(alias)}`, {
    method: 'DELETE',
  });
}

// Bot Configuration
export async function getBotConfig(alias) {
  return request(`/admin/botconfig/${encodeURIComponent(alias)}`);
}

export async function updateBotConfig(alias, config) {
  return request(`/admin/botconfig/${encodeURIComponent(alias)}`, {
    method: 'POST',
    body: JSON.stringify(config),
  });
}

/**
 * Partial config update - safer for single field changes
 * Only updates the fields provided, preserves everything else
 */
export async function patchBotConfig(alias, partialConfig) {
  return request(`/admin/botconfig/${encodeURIComponent(alias)}`, {
    method: 'PATCH',
    body: JSON.stringify(partialConfig),
  });
}

export async function resetBotConfig(alias) {
  return request(`/admin/botconfig/${encodeURIComponent(alias)}`, {
    method: 'DELETE',
  });
}

// Media Upload
export async function uploadMedia(file) {
  const formData = new FormData();
  formData.append('file', file);

  const token = getToken();
  const response = await fetch(`${API_BASE}/admin/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(err.error || 'Upload failed');
  }

  return response.json();
}

// List uploaded media files
export async function listMedia(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/r2${query ? `?${query}` : ''}`);
}

// Delete media file
export async function deleteMedia(key) {
  return request(`/admin/r2/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
}

// Get pending notifications for a user
export async function getPendingNotifications(telegramId, alias) {
  const query = alias ? `?alias=${encodeURIComponent(alias)}` : '';
  return request(`/admin/notifications/user/${encodeURIComponent(telegramId)}${query}`);
}

// Force send a notification
export async function forceNotification(telegramId, notificationType, alias) {
  return request('/admin/notifications/force', {
    method: 'POST',
    body: JSON.stringify({ telegramId, notificationType, alias }),
  });
}

// Send daily stats report manually
export async function sendDailyStats(alias) {
  return request(`/admin/daily-stats/send/${encodeURIComponent(alias)}`, {
    method: 'POST',
  });
}

// ============================================================
// Alert Settings
// ============================================================

// Get alert settings (chatId for notifications)
export async function getAlertSettings() {
  return request('/admin/settings/alerts');
}

// Save alert settings
export async function saveAlertSettings(chatId) {
  return request('/admin/settings/alerts', {
    method: 'PUT',
    body: JSON.stringify({ chatId }),
  });
}

// Send test alert
export async function testAlert() {
  return request('/admin/settings/alerts/test', {
    method: 'POST',
  });
}

// ============================================================
// OSINT Settings
// ============================================================

// Get OSINT settings (enabled, masked API key)
export async function getOsintSettings() {
  return request('/admin/settings/osint');
}

// Save OSINT settings (apiKey, enabled)
export async function saveOsintSettings({ apiKey, enabled }) {
  const body = {};
  if (apiKey !== undefined) body.apiKey = apiKey;
  if (enabled !== undefined) body.enabled = enabled;
  return request('/admin/settings/osint', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

// Retry OSINT lookup for a specific user
export async function retryOsint(telegramId) {
  return request(`/admin/users/${telegramId}/osint-retry`, {
    method: 'POST',
  });
}

// Countries
export async function getCountries(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/countries${query ? `?${query}` : ''}`);
}

export async function backfillCountries() {
  return request('/admin/backfill-countries', { method: 'POST' });
}

// ============================================================
// Broadcast (Mass Notifications)
// ============================================================

export async function getBroadcastCount(alias) {
  return request(`/admin/broadcast/count?alias=${encodeURIComponent(alias)}`);
}

export async function sendBroadcastTest({ message, mediaUrl, mediaType, buttons, alias, telegramId }) {
  return request('/admin/broadcast/test', {
    method: 'POST',
    body: JSON.stringify({ message, mediaUrl, mediaType, buttons, alias, telegramId }),
  });
}

export async function sendBroadcast({ message, mediaUrl, mediaType, buttons, alias }) {
  return request('/admin/broadcast', {
    method: 'POST',
    body: JSON.stringify({ message, mediaUrl, mediaType, buttons, alias }),
  });
}

export async function getBroadcastHistory(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/admin/broadcast/history${query ? `?${query}` : ''}`);
}
